import React, { useState, useCallback } from "react";
import styles from "./ChipsInput.module.css";

/**
 * ChipsInput
 *
 * Controlled chips input used by ConfigsByChips.
 *
 * Responsibilities:
 * - Manage text input
 * - Add chips on Enter
 * - Provide deletion mechanics
 * - Delegate chip rendering via renderChip
 *
 * Explicit non-goals:
 * - Inline editing
 * - Domain-specific logic
 * - Styling beyond basic structure
 */
// export default function ChipsInput({
const ChipsInput = ({
	value = [],
	onChange,

	disabled = false,

	clearInputOnBlur = false,
	disableDeleteOnBackspace = true,

	placeholder = "Type and press enter",

	/**
	 * Optional render prop for custom chip rendering.
	 * If not provided, a default chip is rendered.
	 */
	renderChip,
}) => {
	const [inputValue, setInputValue] = useState("");
	const chips = Array.isArray(value) ? value : [];

	const addChip = useCallback(
		(rawValue) => {
			const next = rawValue.trim();

			if (!next) return;
			if (chips.includes(next)) return;

			onChange([...chips, next]);
			setInputValue("");
		},
		[chips, onChange],
	);

	const removeChip = useCallback(
		(index) => {
			const next = chips.filter((_, i) => i !== index);
			onChange(next);
		},
		[chips, onChange],
	);

	const handleKeyDown = (e) => {
		if (disabled) return;

		if (e.key === "Enter") {
			e.preventDefault();
			addChip(inputValue);
		}

		// Backspace deletion is intentionally disabled by default
		if (e.key === "Backspace" && !disableDeleteOnBackspace && !inputValue && chips.length > 0) {
			onChange(chips.slice(0, -1));
		}
	};

	const handleBlur = () => {
		if (clearInputOnBlur) {
			setInputValue("");
		}
	};

	return (
		<div
			className={`${styles.container} ${disabled ? styles.containerDisabled : ""}`}
			aria-disabled={disabled ? "true" : "false"}
		>
			{chips.map((chip, index) => {
				const onDelete = () => removeChip(index);

				if (renderChip) {
					return renderChip({
						chip,
						index,
						onDelete,
					});
				}

				// Fallback rendering (rarely used in your case)
				return (
					<span key={`${chip}-${index}`} className={styles.chip}>
						<span className={styles.chipLabel}>{chip}</span>
						{!disabled && (
							<button
								type="button"
								className={styles.removeButton}
								onClick={onDelete}
								aria-label={`Delete ${chip}`}
							>
								×
							</button>
						)}
					</span>
				);
			})}

			{!disabled && (
				<input
					type="text"
					className={styles.input}
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					placeholder={placeholder}
				/>
			)}
		</div>
	);
};

export default ChipsInput;
